import { useEffect, useState } from 'react'

import { EMPTY_LIST, EMPTY_STRING } from '../../../../../constants/common'
import { useApi, useNotification } from '../../../../../hooks'
import {
  getRequestTranslationToolbarLanguages,
  getRequestTranslationToolbarTranslatedText,
} from '../../RequestTranslationToolbar.api'

import {
  getRequestTranslationToolbarHtmlValue,
  getRequestTranslationToolbarInitialLanguageOption,
  getRequestTranslationToolbarLanguageOptions,
} from './useRequestTranslationToolbar.utils'

export function useRequestTranslationToolbar({
  requestText,
  ukLanguageCode,
  userLanguageCode,
  onTranslate,
}) {
  const { handleAddErrorNotification } = useNotification()

  const languagesApi = useApi()
  const translateApi = useApi()

  const [fromLanguageOption, setFromLanguageOption] = useState(null)
  const [isLoadingLanguages, setLoadingLanguages] = useState(true)
  const [isTranslating, setTranslating] = useState(false)
  const [languageOptions, setLanguageOptions] = useState(EMPTY_LIST)
  const [toLanguageOption, setToLanguageOption] = useState(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loading flag before an async fetch
    setLoadingLanguages(true)

    languagesApi
      .call(({ options }) =>
        getRequestTranslationToolbarLanguages({
          locale: userLanguageCode,
          signal: options?.signal,
        })
      )
      .then(response => {
        if (response) {
          const nextLanguageOptions =
            getRequestTranslationToolbarLanguageOptions(response)
          const nextFromLanguageOption =
            getRequestTranslationToolbarInitialLanguageOption({
              languageCode: ukLanguageCode,
              languageOptions: nextLanguageOptions,
            })

          setLanguageOptions(nextLanguageOptions)
          setFromLanguageOption(nextFromLanguageOption)
          setToLanguageOption(
            getRequestTranslationToolbarInitialLanguageOption({
              languageCode: userLanguageCode,
              languageOptions: nextLanguageOptions,
            })
          )
        }
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        setLoadingLanguages(false)
      })
  }, [
    handleAddErrorNotification,
    languagesApi,
    ukLanguageCode,
    userLanguageCode,
  ])

  function handleChangeFromLanguageOption(nextFromLanguageOption) {
    setFromLanguageOption(nextFromLanguageOption)
  }

  function handleChangeToLanguageOption(nextToLanguageOption) {
    setToLanguageOption(nextToLanguageOption || null)
  }

  function handleClearFromLanguageOption() {
    handleChangeFromLanguageOption(null)
  }

  function handleClearToLanguageOption() {
    handleChangeToLanguageOption(null)
  }

  function handleSwitchLanguages() {
    if (fromLanguageOption && toLanguageOption) {
      setFromLanguageOption(toLanguageOption)
      setToLanguageOption(fromLanguageOption)
    }
  }

  function handleTranslate() {
    if (fromLanguageOption && toLanguageOption) {
      setTranslating(true)

      translateApi
        .call(({ options }) =>
          getRequestTranslationToolbarTranslatedText({
            from: fromLanguageOption.value,
            signal: options?.signal,
            text: requestText || EMPTY_STRING,
            to: toLanguageOption.value,
          })
        )
        .then(translatedTextResponse => {
          if (translatedTextResponse) {
            onTranslate({
              translatedText: getRequestTranslationToolbarHtmlValue(
                translatedTextResponse
              ),
            })
          }
        })
        .catch(() => {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        })
        .finally(() => {
          setTranslating(false)
        })
    }
  }

  const isTranslateControlsDisabled = isLoadingLanguages || isTranslating
  const isActionDisabled =
    isTranslateControlsDisabled || !fromLanguageOption || !toLanguageOption

  return {
    fromLanguageOption,
    handleChangeFromLanguageOption,
    handleChangeToLanguageOption,
    handleClearFromLanguageOption,
    handleClearToLanguageOption,
    handleSwitchLanguages,
    handleTranslate,
    isActionDisabled,
    isTranslateControlsDisabled,
    isTranslating,
    languageOptions,
    toLanguageOption,
  }
}
